import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", subject: "", message: "" };

const Contact = () => {
    const [form, setForm] = useState(INITIAL);
    const [sending, setSending] = useState(false);

    const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Thank you — your message has been sent. The firm will respond to your enquiry in due course.");
            setForm(INITIAL);
        } catch (err) {
            toast.error("Your message could not be sent. Please try again, or email the firm directly.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#B89047] focus:outline-none transition-colors duration-300";

    return (
        <section id="contact" className="bg-white py-24 lg:py-32">
            <div className="max-w-6xl mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
                    <Reveal>
                        <p className="font-mono-label text-[11px] tracking-[0.3em] uppercase text-[#8E6C2D] mb-5">
                            Contact
                        </p>
                        <h2 className="font-serif-display text-3xl lg:text-4xl font-medium tracking-tight text-[#0B192C] leading-[1.2]">
                            Contact the firm
                        </h2>
                        <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-md">
                            To arrange a consultation or enquire about a legal matter, please email
                            the firm directly or complete the form. All enquiries are treated as
                            confidential.
                        </p>

                        <div className="mt-10 space-y-6">
                            <div className="flex items-start gap-4">
                                <Mail size={20} strokeWidth={1.5} className="text-[#B89047] mt-0.5 shrink-0" aria-hidden="true" />
                                <div>
                                    <p className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-slate-400">
                                        Email
                                    </p>
                                    <a
                                        data-testid="contact-email-link"
                                        href="mailto:danrosengarten01@gmail.com"
                                        className="mt-1 inline-block text-base font-medium text-[#0B192C] underline decoration-[#B89047]/60 underline-offset-4 hover:text-[#8E6C2D] transition-colors duration-300"
                                    >
                                        danrosengarten01@gmail.com
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <MapPin size={20} strokeWidth={1.5} className="text-[#B89047] mt-0.5 shrink-0" aria-hidden="true" />
                                <div>
                                    <p className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-slate-400">
                                        Office
                                    </p>
                                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                                        3 Protea Pl, Sandown
                                        <br />
                                        Sandton, 2196
                                        <br />
                                        South Africa
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <form
                            data-testid="contact-form"
                            onSubmit={submit}
                            className="bg-[#FAF6EE] border border-[#E5D7B5] p-8 lg:p-10"
                            noValidate={false}
                        >
                            <div className="space-y-5">
                                <div>
                                    <label htmlFor="contact-name" className="block font-mono-label text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">
                                        Full Name
                                    </label>
                                    <input
                                        id="contact-name"
                                        data-testid="contact-input-name"
                                        type="text"
                                        required
                                        minLength={2}
                                        value={form.name}
                                        onChange={update("name")}
                                        className={inputCls}
                                        placeholder="Your full name"
                                        autoComplete="name"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="block font-mono-label text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        id="contact-email"
                                        data-testid="contact-input-email"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={update("email")}
                                        className={inputCls}
                                        placeholder="you@example.co.za"
                                        autoComplete="email"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-subject" className="block font-mono-label text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">
                                        Subject
                                    </label>
                                    <input
                                        id="contact-subject"
                                        data-testid="contact-input-subject"
                                        type="text"
                                        required
                                        minLength={2}
                                        value={form.subject}
                                        onChange={update("subject")}
                                        className={inputCls}
                                        placeholder="The nature of your enquiry"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-message" className="block font-mono-label text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">
                                        Message
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        data-testid="contact-input-message"
                                        required
                                        minLength={10}
                                        rows={5}
                                        value={form.message}
                                        onChange={update("message")}
                                        className={`${inputCls} resize-y`}
                                        placeholder="Briefly describe your legal matter. Please do not include sensitive confidential details at this stage."
                                    />
                                </div>
                                <button
                                    data-testid="contact-submit-button"
                                    type="submit"
                                    disabled={sending}
                                    className="w-full inline-flex items-center justify-center gap-2 bg-[#0B192C] text-white text-sm font-semibold tracking-wide px-8 py-4 hover:bg-[#163152] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300 cursor-pointer"
                                >
                                    {sending ? "Sending…" : "Submit Message"}
                                    {!sending && <Send size={15} aria-hidden="true" />}
                                </button>
                                <p className="text-xs text-slate-400 leading-relaxed">
                                    Submitting this form does not create an attorney-client
                                    relationship. Please see the disclaimer below.
                                </p>
                            </div>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default Contact;
